import { cva } from 'class-variance-authority';

import {
  textScaleVariants,
  fontWeightVariants,
  gapScaleVariants,
  focusRingClasses,
} from '@/lib/shared-variants';

/**
 * Dialog overlay (scrim/backdrop) styles
 * Dark semi-transparent overlay behind the dialog
 */
export const dialogOverlayVariants = cva(
  'fixed rounded-lg inset-0 z-50 bg-[rgba(0,0,0,0.85)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
);

/**
 * Dialog content container styles
 * Main dialog panel with shadow and rounded corners
 */
export const dialogContentVariants = cva(
  'fixed left-[50%] top-[50%] z-50 flex flex-col translate-x-[-50%] translate-y-[-50%] bg-surface rounded-[8px] overflow-hidden shadow-[0px_2px_12px_-4px_rgba(0,0,0,0.2),0px_2px_4px_-2px_rgba(0,0,0,0.16)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
  {
    variants: {
      scale: {
        s: 'max-h-[85vh]',
        m: 'max-h-[85vh]',
        l: 'max-h-[90vh]',
      },
      width: {
        s: 'w-full max-w-[480px]',
        m: 'w-full max-w-[768px]',
        l: 'w-full max-w-[1024px]',
      },
    },
    defaultVariants: {
      scale: 'm',
      width: 'm',
    },
  }
);

/**
 * Dialog header styles
 * Contains title and close button
 */
export const dialogHeaderVariants = cva(
  'flex items-center border-b border-outline-subtle bg-surface shrink-0',
  {
    variants: {
      scale: {
        s: `px-sm py-[10px] ${gapScaleVariants.s}`,
        m: `px-md py-[14px] ${gapScaleVariants.m}`,
        l: `px-lg py-[18px] ${gapScaleVariants.l}`,
      },
    },
    defaultVariants: {
      scale: 'm',
    },
  }
);

/**
 * Dialog title styles
 */
export const dialogTitleVariants = cva(
  `flex-1 ${fontWeightVariants.medium} text-foreground leading-[22px]`,
  {
    variants: {
      scale: {
        s: textScaleVariants.s, // 12px
        m: 'text-[16px]', // 16px (from Figma)
        l: textScaleVariants.l, // 16px
      },
    },
    defaultVariants: {
      scale: 'm',
    },
  }
);

/**
 * Dialog description styles
 */
export const dialogDescriptionVariants = cva('text-foreground-secondary', {
  variants: {
    scale: {
      s: textScaleVariants.s,
      m: textScaleVariants.m,
      l: textScaleVariants.l,
    },
  },
  defaultVariants: {
    scale: 'm',
  },
});

/**
 * Dialog body/content area styles
 * Scrollable content area
 */
export const dialogBodyVariants = cva('overflow-y-auto overflow-x-clip bg-surface flex-1', {
  variants: {
    scale: {
      s: 'p-sm',
      m: 'p-md',
      l: 'p-lg',
    },
  },
  defaultVariants: {
    scale: 'm',
  },
});

/**
 * Dialog footer styles
 * Contains action buttons (footer-start and footer-end)
 */
export const dialogFooterVariants = cva(
  'flex items-center justify-between border-t border-outline-subtle bg-surface shrink-0',
  {
    variants: {
      scale: {
        s: `px-sm py-[10px] ${gapScaleVariants.s}`,
        m: `px-md py-[13px] ${gapScaleVariants.m}`,
        l: `px-lg py-[16px] ${gapScaleVariants.l}`,
      },
    },
    defaultVariants: {
      scale: 'm',
    },
  }
);

/**
 * Dialog close button styles
 * X button in header
 */
export const dialogCloseButtonVariants = cva(
  // Sized just past the glyph, and keyboard-only focus — a plain `focus:` ring
  // meant a mouse click lit up the whole box plus its offset.
  `inline-flex items-center justify-center rounded-sm text-foreground-secondary transition-colors hover:bg-surface-muted hover:text-foreground ${focusRingClasses} disabled:pointer-events-none disabled:opacity-50`,
  {
    variants: {
      scale: {
        s: 'size-[24px]',
        m: 'size-[32px]',
        l: 'size-[40px]',
      },
    },
    defaultVariants: {
      scale: 'm',
    },
  }
);

/**
 * Dialog footer start section (left-aligned actions)
 */
export const dialogFooterStartVariants = cva('flex flex-1 items-center justify-start', {
  variants: {
    scale: {
      s: gapScaleVariants.s,
      m: gapScaleVariants.m,
      l: gapScaleVariants.l,
    },
  },
  defaultVariants: {
    scale: 'm',
  },
});

/**
 * Dialog footer end section (right-aligned actions)
 */
export const dialogFooterEndVariants = cva('flex flex-1 items-center justify-end', {
  variants: {
    scale: {
      s: gapScaleVariants.s,
      m: gapScaleVariants.m,
      l: gapScaleVariants.l,
    },
  },
  defaultVariants: {
    scale: 'm',
  },
});

/**
 * Icon size variants for close button
 */
export const dialogIconSizeVariants = {
  s: 'size-[12px]',
  m: 'size-[16px]',
  l: 'size-[20px]',
} as const;
